import React, { useCallback, useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import Footer from "../Footer";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material/";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import AlipayLogo from "../Icons/alipay-logo-2020-svg@3x.webp";
import WeixinLogo from "../Icons/wei-xin-pay-logo@3x.webp";
import GrabLogo from "../Icons/grab-logo.png";
import { Helmet } from "react-helmet";
import common from "../../util/lang/common";
import t__ from "../../util/lang/util";
import ErrorBoundary from "../ErrorBoundary";
import Header from "../Header";
import FaqAnswer from "./FaqAnswer";
import faqs from "../../util/lang/faqs";
import Container from "../Layout/Container";
import { visuallyHidden } from "@mui/utils";
import {
  GRAB_FOOTNOTE_ID,
  TabDisclaimer,
  WEIXIN_FOOTNOTE_ID,
  hasFootnoteMarker,
  renderDescribedByMarker,
} from "./faqFootnotes";

const PREFIX = "Faqs";
const classes = {
  accordion: `${PREFIX}-accordion`,
  accordionQuestion: `${PREFIX}-accordionQuestion`,
  expandIcon: `${PREFIX}-expandIcon`,
  faqAnswer: `${PREFIX}-faqAnswer`,
  faqList: `${PREFIX}-faqList`,
  faqTabPanel: `${PREFIX}-faqTabPanel`,
  tab: `${PREFIX}-tab`,
  tabLabel: `${PREFIX}-tabLabel`,
  tabLogoSlot: `${PREFIX}-tabLogoSlot`,
  tabsSection: `${PREFIX}-tabsSection`,
  tabDisclaimer: `${PREFIX}-tabDisclaimer`,
};

const TAB_KEYS = ["general", "alipay", "weixin", "grab"];

const TAB_LOGOS = {
  alipay: { src: AlipayLogo, alt: "Alipay Logo" },
  weixin: { src: WeixinLogo, alt: "Weixin Pay Logo" },
  grab: { src: GrabLogo, alt: "GrabPay Logo" },
};

const LOGO_HEIGHT = 26;
const LOGO_ROW_HEIGHT = LOGO_HEIGHT;
const LOGO_HEIGHT_MOBILE = 20;
const LOGO_ROW_HEIGHT_MOBILE = LOGO_HEIGHT_MOBILE;

const getTabId = (key) => `faq-tab-${key}`;
const getTabPanelId = (key) => `faq-panel-${key}`;

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.tabsSection}`]: {
    width: "100%",
    "& .MuiTabs-root": {
      overflow: "visible",
    },
    "& .MuiTabs-scroller": {
      overflow: "visible !important",
    },
    [theme.breakpoints.down("md")]: {
      "& .MuiTabs-scroller": {
        overflowX: "auto !important",
        overflowY: "visible !important",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
      },
      "& .MuiTabs-flexContainer": {
        paddingLeft: theme.spacing(5),
        paddingRight: theme.spacing(5),
      },
    },
    [theme.breakpoints.down("lg")]: {
      "& .MuiTabs-flexContainer": {
        gap: theme.spacing(3),
      },
    },
  },
  [`& .${classes.tabLabel}`]: {
    display: "grid",
    gridTemplateRows: `${LOGO_ROW_HEIGHT}px auto`,
    rowGap: theme.spacing(3),
    justifyItems: "center",
    width: "100%",
    "& img": {
      height: LOGO_HEIGHT,
      maxHeight: LOGO_HEIGHT,
      width: "auto",
      maxWidth: "100%",
      objectFit: "contain",
    },
    [theme.breakpoints.down("lg")]: {
      gridTemplateRows: `${LOGO_ROW_HEIGHT_MOBILE}px auto`,
      width: "auto",
      whiteSpace: "nowrap",
      "& img": {
        height: "auto",
        maxHeight: LOGO_HEIGHT_MOBILE,
      },
    },
  },
  [`& .${classes.tabLogoSlot}`]: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: LOGO_ROW_HEIGHT,
    [theme.breakpoints.down("lg")]: {
      height: LOGO_ROW_HEIGHT_MOBILE,
    },
  },
  [`& .${classes.tab}`]: {
    width: 180,
    minHeight: "auto",
    minWidth: "auto",
    padding: 0,
    paddingBottom: theme.spacing(2),
    alignItems: "stretch",
    fontSize: 20,
    lineHeight: "28px",
    color: theme.palette.text.secondary,
    fontWeight: 400,
    textTransform: "none",
    opacity: 1,
    "&.Mui-selected": {
      color: "#303030",
    },
    "&:focus": {
      outline: "none",
    },
    "&:focus:not(:focus-visible)": {
      outline: "none",
    },
    "&.Mui-focusVisible, &:focus-visible": {
      outline: "none",
      borderRadius: 4,
      boxShadow: `inset 0 0 0 2px ${theme.palette.focus}`,
    },
    [theme.breakpoints.down("lg")]: {
      fontSize: theme.typography.body1.fontSize,
      lineHeight: 1.4,
      width: "auto",
      flexShrink: 0,
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
  [`& .${classes.faqTabPanel}`]: {
    width: "100%",
    maxWidth: 1120,
    marginLeft: "auto",
    marginRight: "auto",
  },
  [`& .${classes.tabDisclaimer}`]: {
    width: "100%",
    maxWidth: 1120,
    marginLeft: "auto",
    marginRight: "auto",
    marginTop: theme.spacing(4),
    paddingLeft: theme.spacing(7),
    paddingRight: theme.spacing(7),
    color: theme.palette.text.secondary,
    fontSize: 12,
    lineHeight: 1.5,
    [theme.breakpoints.up("lg")]: {
      fontSize: 14,
    },
    [theme.breakpoints.down("sm")]: {
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5),
    },
  },
  [`& .${classes.faqList}`]: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    width: "100%",
  },
  [`& .${classes.accordion}`]: {
    display: "block",
    margin: 0,
    padding: 0,
    boxShadow: "none",
    borderRadius: 0,
    borderBottom: "1px solid #cacaca",
    "&:before": {
      display: "none",
    },
    "&.Mui-expanded": {
      margin: 0,
    },
    "& .MuiAccordionSummary-root": {
      minHeight: 90,
      paddingLeft: 24,
      paddingRight: 24,
      paddingTop: 6,
      paddingBottom: 6,
      "&:focus": {
        outline: "none",
      },
      "&:focus:not(:focus-visible)": {
        outline: "none",
      },
      "&.Mui-focusVisible, &:focus-visible": {
        outline: "none",
        boxShadow: `inset 0 0 0 2px ${theme.palette.focus}`,
      },
      [theme.breakpoints.down("lg")]: {
        minHeight: 72,
        paddingLeft: 16,
        paddingRight: 16,
      },
    },
    "& .MuiAccordionSummary-content": {
      margin: "12px 0",
      flex: 1,
      marginRight: theme.spacing(3),
      [theme.breakpoints.down("lg")]: {
        marginRight: theme.spacing(2),
      },
    },
    "& .MuiAccordionDetails-root": {
      paddingTop: 0,
      paddingBottom: 24,
      paddingLeft: 24,
      // Match question text inset: content margin + icon gap + icon width
      paddingRight: `calc(24px + 36px + ${theme.spacing(2)} + ${theme.spacing(3)})`,
      [theme.breakpoints.down("lg")]: {
        paddingBottom: 20,
        paddingLeft: 16,
        paddingRight: `calc(16px + 36px + ${theme.spacing(2)} + ${theme.spacing(2)})`,
      },
    },
  },
  [`& .${classes.accordionQuestion}`]: {
    margin: 0,
    fontSize: 24,
    lineHeight: 1.25,
    fontWeight: 400,
    color: theme.palette.text.primary,
    [theme.breakpoints.down("lg")]: {
      fontSize: 16,
    },
  },
  [`& .MuiAccordionSummary-expandIconWrapper`]: {
    transform: "none !important",
    flexShrink: 0,
    marginLeft: theme.spacing(2),
  },
  [`& .${classes.expandIcon}`]: {
    color: theme.palette.text.primary,
    fontSize: 36,
  },
}));

const FaqTabLabel = ({ tabKey, label, describedByMarker }) => {
  const logo = TAB_LOGOS[tabKey];

  const labelContent = describedByMarker
    ? renderDescribedByMarker(label)
    : label;

  return (
    <Box className={classes.tabLabel}>
      <Box className={classes.tabLogoSlot} aria-hidden={!logo}>
        {logo ? <Box component="img" src={logo.src} alt={logo.alt} /> : null}
      </Box>
      <Box component="span">{labelContent}</Box>
    </Box>
  );
};

const Faqs = () => {
  const theme = useTheme();
  const mdUp = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const [activeTab, setActiveTab] = useState(0);
  const [expanded, setExpanded] = useState([]);

  const lang = theme.lang && faqs[theme.lang] ? theme.lang : "en";
  const faqData = faqs[lang];
  const fallbackQuestions = faqs.en.questions;
  const footnoteLabel = faqData.footnoteLabel ?? faqs.en.footnoteLabel;
  const grabFootnote = {
    id: GRAB_FOOTNOTE_ID,
  };

  const getQuestionsForTab = (key) => {
    const questions = faqData.questions[key];
    return questions?.length > 0 ? questions : fallbackQuestions[key] || [];
  };

  const handleAccordionChange = (panel) => (_, isExpanded) => {
    setExpanded((prev) =>
      isExpanded ? [...prev, panel] : prev.filter((id) => id !== panel)
    );
  };

  const handleTabLinkClick = useCallback((tabKey) => {
    const tabIndex = TAB_KEYS.indexOf(tabKey);
    if (tabIndex === -1) return;

    setActiveTab(tabIndex);
    setExpanded([]);
    window.history.replaceState(null, "", `#${getTabId(tabKey)}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const syncTabFromHash = () => {
      const hash = window.location.hash.slice(1);
      if (!hash.startsWith("faq-tab-")) return;

      const tabKey = hash.replace("faq-tab-", "");
      const tabIndex = TAB_KEYS.indexOf(tabKey);
      if (tabIndex === -1) return;

      setActiveTab(tabIndex);
      setExpanded([]);
      window.scrollTo({ top: 0, behavior: "auto" });
    };

    syncTabFromHash();
    window.addEventListener("hashchange", syncTabFromHash);
    return () => window.removeEventListener("hashchange", syncTabFromHash);
  }, []);

  const renderFaqAccordions = (key) => {
    const questions = getQuestionsForTab(key);
    const footnote = key === "grab" ? grabFootnote : null;

    return questions.map((item, index) => {
      const panelId = `faq-${key}-${index}`;
      const headerId = `${panelId}-header`;
      const contentId = `${panelId}-content`;
      const questionHasFootnote = hasFootnoteMarker(item.question);

      return (
        <Accordion
          key={panelId}
          component="li"
          className={classes.accordion}
          expanded={expanded.includes(panelId)}
          onChange={handleAccordionChange(panelId)}
          disableGutters
          elevation={0}
        >
          <AccordionSummary
            expandIcon={
              expanded.includes(panelId) ? (
                <RemoveIcon className={classes.expandIcon} />
              ) : (
                <AddIcon className={classes.expandIcon} />
              )
            }
            aria-controls={contentId}
            aria-describedby={
              questionHasFootnote ? GRAB_FOOTNOTE_ID : undefined
            }
            id={headerId}
          >
            <Typography
              className={classes.accordionQuestion}
              component="div"
              role="heading"
              aria-level={3}
            >
              {item.question}
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            id={contentId}
            role="region"
            aria-labelledby={headerId}
          >
            <Box className={classes.faqAnswer}>
              <FaqAnswer
                content={item.content ?? item.answer}
                footnote={footnote && index > 0 ? footnote : null}
                onTabLinkClick={handleTabLinkClick}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      );
    });
  };

  return (
    <Root>
      <Helmet>
        <title>Frequently Asked Questions</title>
        <meta
          name="description"
          content="Find answers to common questions about using your card in popular destinations like China, Singapore, Thailand, and more."
        />

        <link
          rel="canonical"
          href="https://travel.discoverglobalnetwork.com/global-acceptance/faqs"
        />
      </Helmet>
      <Box sx={{ overflowX: "clip" }}>
        <ErrorBoundary>
          <Header />
          <main>
            <Box
              width="100%"
              height={{ xs: "275px", md: "375px" }}
              backgroundColor={theme.palette.secondary.light}
              color="common.white"
              py={{ xs: 10, md: "80px" }}
              px={{ xs: 5, md: 0 }}
              display="flex"
              justifyContent="center"
            >
              <Box
                sx={{
                  marginY: mdUp ? theme.spacing(20) : theme.spacing(8),
                  marginX: theme.spacing(5),
                }}
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
              >
                <Typography
                  variant="h1_variant"
                  align="center"
                  fontWeight={700}
                  maxWidth={mdUp ? "none" : "330px"}
                  component="h1"
                >
                  {faqData.headline}
                </Typography>
                <Typography
                  variant={mdUp ? "h3" : "h4"}
                  align="center"
                  maxWidth={930}
                  marginTop={theme.spacing(4)}
                  component="p"
                  lineHeight={1.4}
                >
                  {faqData.subheadline}
                </Typography>
              </Box>
            </Box>

            <Box
              bgcolor="common.white"
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={{ xs: 4, md: 16 }}
              sx={{
                pt: { xs: 10, md: 20 },
                pb: { xs: 10, md: 20 },
                width: "100%",
              }}
            >
              <Typography component="h2" sx={visuallyHidden}>
                Frequently Asked Questions
              </Typography>
              <Box className={classes.tabsSection}>
                <Tabs
                  value={activeTab}
                  onChange={(_, value) => {
                    setActiveTab(value);
                    setExpanded([]);
                  }}
                  aria-label={faqData.tabsAriaLabel || faqs.en.tabsAriaLabel}
                  variant={mdUp ? "standard" : "scrollable"}
                  scrollButtons={false}
                  sx={{
                    width: "100%",
                    minHeight: "unset",
                    overflow: "visible",
                    "& .MuiTabs-scroller": {
                      overflow: "visible",
                    },
                    "& .MuiTabs-indicator": {
                      backgroundColor: theme.palette.secondary.light,
                      height: 4,
                    },
                    "& .MuiTabs-flexContainer": {
                      gap: mdUp ? "32px" : undefined,
                      justifyContent: mdUp ? "center" : "flex-start",
                      alignItems: "flex-end",
                    },
                  }}
                >
                  {TAB_KEYS.map((key) => (
                    <Tab
                      key={key}
                      id={getTabId(key)}
                      aria-controls={getTabPanelId(key)}
                      aria-describedby={
                        key === "weixin" ? WEIXIN_FOOTNOTE_ID : undefined
                      }
                      className={classes.tab}
                      disableRipple
                      label={
                        <FaqTabLabel
                          tabKey={key}
                          label={faqData.tabs[key]}
                          describedByMarker={key === "weixin"}
                        />
                      }
                    />
                  ))}
                </Tabs>
              </Box>

              <Container bgcolor="common.white" py={0} width="100%">
                {TAB_KEYS.map((key, index) => (
                  <Box
                    key={key}
                    component="section"
                    role="tabpanel"
                    id={getTabPanelId(key)}
                    aria-labelledby={getTabId(key)}
                    hidden={activeTab !== index}
                    tabIndex={activeTab === index ? 0 : -1}
                    className={classes.faqTabPanel}
                  >
                    <Box component="ul" className={classes.faqList}>
                      {renderFaqAccordions(key)}
                    </Box>
                    {key === "grab" && (
                      <TabDisclaimer
                        id={GRAB_FOOTNOTE_ID}
                        ariaLabel={footnoteLabel}
                        className={classes.tabDisclaimer}
                      >
                        {faqData.grabTabDisclaimer ?? faqs.en.grabTabDisclaimer}
                      </TabDisclaimer>
                    )}
                  </Box>
                ))}
              </Container>

              <TabDisclaimer
                id={WEIXIN_FOOTNOTE_ID}
                marker="*"
                ariaLabel={footnoteLabel}
                className={classes.tabDisclaimer}
              >
                {faqData.weixinTabDisclosure ?? faqs.en.weixinTabDisclosure}
              </TabDisclaimer>
            </Box>
          </main>
          <Footer />
        </ErrorBoundary>
      </Box>
    </Root>
  );
};

export default Faqs;
